"use client";

import type { DiscoverSelection } from "@/components/discover/filters/selection";
import ActivitiesDropdown from "@/app/ActivitiesDropdown";
import LocationSelector from "@/components/location-selector/LocationSelector";
import { SearchButton } from "@/components/SearchButton";
import { SearchButtonWithRedirect } from "@/components/SearchButtonWithRedirect";
import { Separator } from "@/components/ui/separator";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { cn } from "@/lib/utils";
import type { Coords } from "@/types/places";
import type { CustomLocation, MapAreaId } from "@/utils/location";
import { useCallback, useEffect, useRef, useState } from "react";

export type DesktopSearchBarProps = {
  currentArea: MapAreaId;
  currentCustomLocation?: CustomLocation | null;
  coords: Coords;
  selection: DiscoverSelection;
  redirectToHome?: boolean;
};

/**
 * Desktop search bar component displayed in the site header.
 * For mobile, use SearchPreviewBar instead.
 */
export function DesktopSearchBar({
  currentArea,
  currentCustomLocation,
  coords,
  selection,
  redirectToHome = false,
}: DesktopSearchBarProps) {
  const { isAnyTriggerOpen, activeTrigger, initialTrigger } =
    useSearchTriggerContext();

  // State for controlled LocationSelector
  const [isLocationSelectorOpen, setLocationSelectorOpen] = useState(false);

  // Ref for auto-open timeout orchestration
  const autoOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoOpenTimeoutRef.current) {
        clearTimeout(autoOpenTimeoutRef.current);
      }
    };
  }, []);

  // Handle activity selection - auto-open location if activity was clicked first
  const handleActivitySelected = useCallback(() => {
    if (initialTrigger === "activity") {
      // Clear any pending timeout
      if (autoOpenTimeoutRef.current) {
        clearTimeout(autoOpenTimeoutRef.current);
      }
      // Short delay allows Activity collapse and Location expand to overlap,
      // creating a smooth "morph" effect with the existing CSS transitions (300ms)
      autoOpenTimeoutRef.current = setTimeout(() => {
        setLocationSelectorOpen(true);
      }, 75);
    }
  }, [initialTrigger]);

  const showSeparator = activeTrigger === null && !isAnyTriggerOpen;

  // Both wrappers share space equally when expanded. This keeps the location
  // icon stable across ALL expanded states (active, gap, pending). The ~25px
  // shift on initial open is imperceptible during the bar's height/color transition.
  const expandableItemClass = isAnyTriggerOpen
    ? "flex-1 basis-0 min-w-0"
    : "shrink-0 basis-auto";

  return (
    <div
      data-search-bar-component="container"
      className={cn(
        "flex rounded-full items-center overflow-hidden",
        // Container width is auto in both states — only height, bg, padding,
        // and border actually transition (width: auto → auto doesn't fire).
        "transition-[height,background-color,padding,border] duration-300 ease-out",
        isAnyTriggerOpen
          ? "bg-accent px-2 py-1 h-16"
          : "bg-white border border-gray-200 p-1 h-12",
      )}
    >
      <div
        className={cn(
          "flex items-center min-w-0",
          isAnyTriggerOpen && "w-[450px]",
        )}
      >
        <div className={cn("overflow-hidden", expandableItemClass)}>
          <ActivitiesDropdown
            coords={coords}
            selection={selection}
            onActivitySelected={handleActivitySelected}
          />
        </div>

        <Separator
          orientation="vertical"
          aria-hidden={!showSeparator}
          className={cn(
            "w-px h-8 bg-gray-200 shrink-0 transition-opacity duration-150 ease-out",
            showSeparator ? "opacity-100" : "opacity-0",
          )}
        />

        <div className={cn("overflow-hidden", expandableItemClass)}>
          <LocationSelector
            currentArea={currentArea}
            currentCustomLocation={currentCustomLocation}
            isOpen={isLocationSelectorOpen}
            onOpenChange={setLocationSelectorOpen}
          />
        </div>
      </div>
      {redirectToHome ? (
        <SearchButtonWithRedirect
          className={cn("ml-2 shrink-0", isAnyTriggerOpen ? "h-12" : "h-10")}
          forceExpanded={isAnyTriggerOpen}
          fallbackArea={currentArea}
        />
      ) : (
        <SearchButton
          className={cn("ml-2 shrink-0", isAnyTriggerOpen ? "h-12" : "h-10")}
          forceExpanded={isAnyTriggerOpen}
        />
      )}
    </div>
  );
}
